import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    CheckCircle2,
    FileCheck,
    Loader2,
    RefreshCw,
    ShieldCheck,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Sigorta dosyası evrak sekmesinde vekalet ↔ vekaletler modülü köprüsü.
 */
export default function SigortaVekaletBridge({ dosyaId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    const load = useCallback(async () => {
        if (!dosyaId) return;
        setLoading(true);
        try {
            const r = await api.get(
                `/v1/admin/sigorta-dosyalari/${dosyaId}/vekalet-kopru/`,
            );
            setData(r.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [dosyaId]);

    useEffect(() => {
        load();
    }, [load]);

    const senkron = async () => {
        setSyncing(true);
        try {
            const r = await api.post(
                `/v1/admin/sigorta-dosyalari/${dosyaId}/vekalet-senkron/`,
            );
            setData(r.data);
            toast.success(
                `${r.data?.olusturulan || 0} vekalet dosyası modüle aktarıldı`,
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Vekalet senkronu başarısız",
            );
        } finally {
            setSyncing(false);
        }
    };

    if (loading) {
        return (
            <div className="mb-4 flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Vekalet köprüsü yükleniyor…
            </div>
        );
    }

    if (!data) return null;

    const vekaletler = data.vekaletler || [];
    const evraklar = data.evraklar || [];
    const bekleyen = evraklar.filter((e) => !e.senkron).length;
    const muDurum = data.muvekkil_vekalet_durumu;

    return (
        <div className="mb-4 rounded-xl border border-violet-500/25 bg-gradient-to-br from-violet-500/10 via-[#0B0F19]/90 to-[#121A2F]/90 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-lg bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                        <FileCheck className="h-4 w-4 text-violet-300" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-slate-100">
                            Vekaletler Modülü Köprüsü
                        </div>
                        <div className="text-[11px] text-slate-500">
                            Sigorta evrakındaki vekaletler otomatik vekaletler
                            kaydına aktarılır
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {bekleyen > 0 && (
                        <button
                            type="button"
                            disabled={syncing}
                            onClick={senkron}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600/80 hover:bg-violet-600 text-white text-xs font-semibold disabled:opacity-50"
                        >
                            {syncing ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <RefreshCw className="h-3.5 w-3.5" />
                            )}
                            {bekleyen} vekalet senkronla
                        </button>
                    )}
                    {vekaletler[0]?.id && (
                        <Link
                            to={`/e/vekaletler/${vekaletler[0].id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-violet-500/35 text-violet-200 text-xs font-semibold hover:bg-violet-500/10"
                        >
                            Vekalet kaydı
                            <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    )}
                </div>
            </div>

            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat
                    label="Vekalet evrak"
                    value={data.vekalet_evrak_sayisi ?? 0}
                    tone="violet"
                />
                <Stat
                    label="Modüle aktarılan"
                    value={data.senkron_dosya_sayisi ?? 0}
                    tone="emerald"
                />
                <Stat
                    label="Müvekkil vekalet"
                    value={muDurum === "ALINDI" ? "Alındı" : "Eksik"}
                    tone={muDurum === "ALINDI" ? "emerald" : "amber"}
                />
                <Stat
                    label="Vekalet kaydı"
                    value={vekaletler.length}
                    tone="sky"
                />
            </div>

            {evraklar.length > 0 && (
                <div className="px-4 pb-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">
                        Vekalet evrakları
                    </div>
                    <div className="space-y-2">
                        {evraklar.map((ev) => (
                            <div
                                key={ev.evrak_id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-[#121A2F]/80 px-3 py-2"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    {ev.senkron ? (
                                        <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                                    ) : (
                                        <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
                                    )}
                                    <span className="text-xs text-slate-300">
                                        Vekalet evrak #{ev.evrak_id}
                                    </span>
                                    <span className="text-[10px] text-slate-500">
                                        {ev.yuklenme_tarihi
                                            ? new Date(
                                                  ev.yuklenme_tarihi,
                                              ).toLocaleString("tr-TR")
                                            : ""}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {ev.senkron && ev.vekalet_id ? (
                                        <Link
                                            to={`/e/vekaletler/${ev.vekalet_id}`}
                                            className="text-[10px] font-semibold text-violet-300 hover:underline inline-flex items-center gap-1"
                                        >
                                            <ShieldCheck className="h-3 w-3" />
                                            Vekaletler modülünde
                                        </Link>
                                    ) : (
                                        <span className="text-[10px] text-amber-300">
                                            Senkron bekliyor
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, tone }) {
    const tones = {
        violet: "border-violet-500/25 bg-violet-500/10 text-violet-200",
        emerald: "border-emerald-500/25 bg-emerald-500/10 text-emerald-200",
        amber: "border-amber-500/25 bg-amber-500/10 text-amber-200",
        sky: "border-sky-500/25 bg-sky-500/10 text-sky-200",
    };
    return (
        <div
            className={`rounded-lg border px-3 py-2 ${tones[tone] || tones.violet}`}
        >
            <div className="text-[10px] uppercase tracking-wide opacity-80">
                {label}
            </div>
            <div className="text-lg font-bold mt-0.5">{value}</div>
        </div>
    );
}
